import { BrowserRouter, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback, Suspense } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { AppRoutes } from './router';
import { useAuth } from './hooks/useAuth';
import { AccentColorProvider } from './contexts/AccentColorContext';
import { useInactivityLock } from './hooks/useInactivityLock';
import InactivityLockModal from './components/feature/InactivityLockModal';
import InactivityWarningBanner from './components/feature/InactivityWarningBanner';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Shortcut {
  keys: string;
  label: string;
  icon: string;
  category: string;
  adminOnly?: boolean;
}

const ALL_SHORTCUTS: Shortcut[] = [
  { keys: 'Alt+D', label: 'Tableau de bord', icon: 'ri-dashboard-line', category: 'Navigation' },
  { keys: 'Alt+H', label: 'Mon Espace', icon: 'ri-user-heart-line', category: 'Navigation' },
  { keys: 'Alt+N', label: 'Notifications & Alertes', icon: 'ri-notification-3-line', category: 'Navigation' },
  { keys: 'Alt+S', label: 'Recherche globale', icon: 'ri-search-line', category: 'Navigation' },
  { keys: 'Ctrl+K', label: 'Recherche globale', icon: 'ri-search-line', category: 'Navigation' },
  { keys: 'Alt+P', label: 'Paramètres', icon: 'ri-equalizer-2-line', category: 'Administration', adminOnly: true },
  { keys: 'Alt+?', label: 'Aide raccourcis clavier', icon: 'ri-keyboard-line', category: 'Aide' },
];

// ─── Modale d'aide ────────────────────────────────────────────────────────────
function ShortcutsHelpModal({ isOpen, onClose, userRole }: { isOpen: boolean; onClose: () => void; userRole?: string }) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const visibleShortcuts = ALL_SHORTCUTS.filter(s => !s.adminOnly || userRole === 'admin');
  const categories = [...new Set(visibleShortcuts.map(s => s.category))];

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[9998] backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto overflow-hidden"
          style={{ border: '1px solid #e5e7eb' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-900">
                <i className="ri-keyboard-line text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Raccourcis clavier</h2>
                <p className="text-xs text-gray-500">Naviguez plus vite avec le clavier</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-5 max-h-[60vh] overflow-y-auto">
            {categories.map(cat => (
              <div key={cat}>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">{cat}</p>
                <div className="space-y-1">
                  {visibleShortcuts.filter(s => s.category === cat).map(s => (
                    <div
                      key={s.keys}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200">
                          <i className={`${s.icon} text-sm text-gray-600`}></i>
                        </div>
                        <span className="text-sm text-gray-700">{s.label}</span>
                        {s.adminOnly && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600 font-medium">Admin</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {s.keys.split('+').map((k, i) => (
                          <span key={i} className="flex items-center gap-1">
                            {i > 0 && <span className="text-gray-300 text-xs">+</span>}
                            <kbd className="inline-flex items-center px-2 py-1 text-[11px] font-mono font-semibold text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm">
                              {k}
                            </kbd>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
            <span className="text-xs text-gray-400">Appuyez sur <kbd className="bg-white border border-gray-300 rounded px-1 text-[11px] font-mono">Esc</kbd> pour fermer</span>
            <span className="text-xs text-gray-400">{visibleShortcuts.length} raccourcis disponibles</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Toast notification ───────────────────────────────────────────────────────
function ShortcutToast({ message, icon }: { message: string; icon: string }) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] animate-fade-in">
      <div className="flex items-center gap-2 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-full shadow-lg whitespace-nowrap">
        <i className={`${icon} text-teal-400`}></i>
        <span>{message}</span>
      </div>
    </div>
  );
}

// ─── Gestionnaire de raccourcis ───────────────────────────────────────────────
function KeyboardShortcuts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [toast, setToast] = useState<{ message: string; icon: string } | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const showToast = useCallback((message: string, icon: string) => {
    setToast({ message, icon });
    setTimeout(() => setToast(null), 2200);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorer si focus dans un input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;

      if (e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'd':
            e.preventDefault();
            navigate('/dashboard');
            showToast('Tableau de bord', 'ri-dashboard-line');
            break;
          case 'h':
            e.preventDefault();
            navigate('/mon-espace');
            showToast('Mon Espace', 'ri-user-heart-line');
            break;
          case 'n':
            e.preventDefault();
            navigate('/notifications-alertes');
            showToast('Notifications & Alertes', 'ri-notification-3-line');
            break;
          case 's':
            e.preventDefault();
            // Déclencher la recherche globale via Ctrl+K
            document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }));
            break;
          case 'p':
            if (user?.role === 'admin') {
              e.preventDefault();
              navigate('/parametres');
              showToast('Paramètres', 'ri-equalizer-2-line');
            }
            break;
          case '?':
            e.preventDefault();
            setShowHelp(true);
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, user, showToast]);

  return (
    <>
      {toast && <ShortcutToast message={toast.message} icon={toast.icon} />}
      <ShortcutsHelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} userRole={user?.role} />
    </>
  );
}

// ─── Session security (inactivité) ───────────────────────────────────────────
// Doit être à l'intérieur de AuthProvider mais EN DEHORS de chaque AppLayout
// pour que le timer ne soit jamais réinitialisé lors des navigations entre pages.
function SessionSecurityProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  const {
    isLocked,
    showWarning,
    secondsRemaining,
    inactivityMinutes,
    logoutAfterLockMinutes,
    unlock,
    resetTimer,
  } = useInactivityLock(!!user);

  // La page d'accueil et la page de connexion ne doivent jamais afficher
  // l'écran de verrouillage / la bannière d'avertissement (pages publiques,
  // consultables même avec une session ouverte en arrière-plan).
  const hideLockUI = location.pathname === '/' || location.pathname === '/login';

  return (
    <>
      {children}

      {/* Bannière d'avertissement — 60 s avant verrouillage */}
      <InactivityWarningBanner
        show={showWarning && !isLocked && !hideLockUI}
        secondsRemaining={secondsRemaining}
        onDismiss={resetTimer}
      />

      {/* Écran de verrouillage */}
      <InactivityLockModal
        isLocked={isLocked && !hideLockUI}
        onUnlock={unlock}
        inactivityMinutes={inactivityMinutes}
        logoutAfterLockMinutes={logoutAfterLockMinutes}
      />
    </>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter basename={__BASE_PATH__}>
      <AuthProvider>
        <AccentColorProvider>
          <SessionSecurityProvider>
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
                  <p className="text-gray-600">Chargement...</p>
                </div>
              </div>
            }>
              <AppRoutes />
            </Suspense>
            <KeyboardShortcuts />
          </SessionSecurityProvider>
        </AccentColorProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
